<template>
	<div class="documentation-page-root" style="width: 100%; height: 100vh; display: flex; flex-direction: column; margin: 0; padding: 0; background-color: #fff;">

		<!-- Custom Documentation Header -->
		<div
			style="display: flex; justify-content: space-between; align-items: center; padding: 0 20px; height: 60px; border-bottom: 1px solid #ebeef5; background-color: #fff; flex-shrink: 0; position: relative; z-index: 1001;">
			<div style="display: flex; align-items: center;">
				<i class="el-icon-s-fold mobile-menu-btn" @click="toggleSidebar"></i>
				<div style="display: flex; align-items: center; gap: 10px; cursor: pointer;" @click="$router.push('/')">
					<img src="../assets/primaryLogo.svg" style="height: 28px; object-fit: contain;" />
					<span style="font-size: 16px; font-weight: 600; color: #1f2d3d; margin-left: 5px;" class="hide-on-mobile">Documentation</span>
				</div>
			</div>
			<div style="display: flex; align-items: center; gap: 10px;">
				<el-autocomplete
					clearable
					placeholder="Search integrations"
					prefix-icon="el-icon-search"
					v-model="integrationSearchQuery"
					:fetch-suggestions="querySearch"
					@select="handleSearchSelect"
					style="max-width: 250px; width: 100%;"
					size="small"
					class="docs-search"
					value-key="displayName"
				></el-autocomplete>
			</div>
		</div>

		<el-container style="flex: 1; overflow: hidden; margin: 0; background-color: #fff; position: relative;">
			<div v-if="showSidebar && isMobileView" class="sidebar-overlay" @click="showSidebar = false"></div>
			<el-aside class="docs-sidebar" :class="{ 'sidebar-open': showSidebar }" style="width: 280px; height: 100%; border-right: 1px solid #ebeef5;">
				<div class="integration-list" style="height: 100%; overflow-y: auto; padding-top: 15px;"
					v-loading="loadingIntegrations" element-loading-text="Loading integrations...">
					<div v-for="integration in filteredIntegrations" :key="integration.nodesObjectPropertyName"
						class="integration-item"
						:class="{ 'active': selectedIntegration === integration.nodesObjectPropertyName }"
						@click="selectIntegration(integration.nodesObjectPropertyName)">
						<img v-if="integration.icon" class="integration-icon" :src="`data:image/${integration.iconMimeType === 'png' ? 'png' : 'svg+xml'};base64, ` + integration.icon
							" />
						<img v-if="!integration.icon" class="integration-icon" src="../assets/node.svg" />
						<span>{{ integration.displayName }}</span>
					</div>
				</div>
			</el-aside>
			<el-container style="height: 100%;">
				<el-main style="padding: 20px; overflow-y: auto; height: 100%; text-align: left;">
					<div v-if="loadingDocs" class="loading-state">
						<i class="el-icon-loading" style="font-size: 24px;"></i>
						<p>Loading documentation...</p>
					</div>
					<div v-else-if="!selectedIntegration" style="max-width: 740px; padding-top: 10px; margin: 0 auto;">
						<h1
							style="font-size: 28px; font-weight: 600; color: #1f2d3d; margin-bottom: 24px; border-bottom: 1px solid #ebeef5; padding-bottom: 12px; margin-top: 0;">
							Introduction</h1>
						<p style="font-size: 15px; color: #303133; line-height: 1.6; margin-bottom: 16px;">
							Welcome to the Centiloquy documentation guide — your complete resource for building powerful, no-code automations. 
							Centiloquy is an advanced, AI-driven automation platform designed to seamlessly connect your favorite web applications, 
							services, and tools, allowing you to streamline complex workflows and eliminate repetitive manual tasks.
						</p>

						<h2 style="font-size: 20px; font-weight: 600; color: #1f2d3d; margin-top: 32px; margin-bottom: 16px;">What can Centiloquy do?</h2>
						<p style="font-size: 15px; color: #303133; line-height: 1.6; margin-bottom: 16px;">
							Unlike traditional automation tools, Centiloquy goes beyond simple "If This Then That" logic. By leveraging 
							state-of-the-art Artificial General Intelligence (AGI) and a massive library of native integrations, Centiloquy can 
							autonomously reason through multi-step workflows, process natural language, generate code dynamically, and execute 
							intricate business logic.
						</p>

						<ul style="font-size: 15px; color: #303133; line-height: 1.6; margin-bottom: 24px; padding-left: 20px;">
							<li style="margin-bottom: 8px;"><strong>Intelligent Chat & AGI Agents:</strong> Tell the AI what you want to achieve in plain English, and it will automatically build, configure, and refine the workflow for you.</li>
							<li style="margin-bottom: 8px;"><strong>Hundreds of Integrations:</strong> Connect with platforms like Stripe, Slack, Gmail, Shopify, Airtable, and dozens of specialized LLMs instantly.</li>
							<li style="margin-bottom: 8px;"><strong>Advanced AI Capabilities:</strong> Directly utilize models like OpenAI, Claude, and specialized private LLMs to analyze text, extract data, generate images, or even synthesize video natively within your flows.</li>
							<li style="margin-bottom: 8px;"><strong>Custom Code Execution:</strong> Run custom Python or Node.js scripts securely in our AI Code Sandbox whenever you need complex data transformations beyond standard no-code blocks.</li>
							<li style="margin-bottom: 8px;"><strong>Dynamic Webhooks & UI Generation:</strong> Trigger workflows from external events or generate dynamic HTML/Vue interfaces on the fly to interact with your users.</li>
						</ul>

						<h2 style="font-size: 20px; font-weight: 600; color: #1f2d3d; margin-top: 32px; margin-bottom: 16px;">How to use this guide</h2>
						<p style="font-size: 15px; color: #303133; line-height: 1.6; margin-bottom: 16px;">
							Use the sidebar on the left to browse documentation for specific integrations. Each integration page details the 
							available triggers, actions, required credentials, and data structures. Whether you're setting up a simple email 
							auto-responder or building a complete AI customer service agent, this guide will provide the technical details you need.
						</p>
					</div>
					<div v-else style="position: relative; max-width: 740px; margin: 0 auto;">
						<el-button icon="el-icon-document-copy" size="small" @click="copyDocumentation" class="copy-btn"
							style="position: absolute; right: 0; top: 0; z-index: 10;">
							Copy
						</el-button>
						<div class="markdown-content" v-html="renderedMarkdown"></div>
					</div>
				</el-main>
			</el-container>
		</el-container>
	</div>
</template>

<script>
import { marked } from "marked";

export default {
	components: {
	},
	data() {
		return {
			integrationSearchQuery: "",
			availableIntegrations: [],
			selectedIntegration: null,
			documentationContent: "",
			loadingDocs: false,
			loadingIntegrations: true,
			showSidebar: false,
			isMobileView: false,
		};
	},
	computed: {
		filteredIntegrations() {
			const internalOnly = ["aiIntegrationSandbox", "aiWorkflowTemplate"];
			let list = (this.availableIntegrations || []).filter(item => !internalOnly.includes(item.nodesObjectPropertyName));
			// Sort alphabetically
			return list.sort((a, b) => a.displayName.localeCompare(b.displayName));
		},
		renderedMarkdown() {
			return marked.parse(this.documentationContent || "");
		}
	},
	async mounted() {
		this.checkMobile();
		window.addEventListener("resize", this.checkMobile);
		
		this.loadingIntegrations = true;
		await this.returnAllNodes();
		this.loadingIntegrations = false;
		if (this.$route.query.integration) {
			this.selectIntegration(this.$route.query.integration);
		}
	},
	beforeDestroy() {
		window.removeEventListener("resize", this.checkMobile);
	},
	methods: {
		querySearch(queryString, cb) {
			const internalOnly = ["aiIntegrationSandbox", "aiWorkflowTemplate"];
			const links = (this.availableIntegrations || []).filter(integration => !internalOnly.includes(integration.nodesObjectPropertyName));
			const results = queryString ? links.filter(integration => integration.displayName.toLowerCase().includes(queryString.toLowerCase())) : links;
			cb(results);
		},
		handleSearchSelect(item) {
			this.selectIntegration(item.nodesObjectPropertyName);
		},
		checkMobile() {
			this.isMobileView = window.innerWidth <= 768;
			if (!this.isMobileView) {
				this.showSidebar = false;
			}
		},
		toggleSidebar() {
			this.showSidebar = !this.showSidebar;
		},
		async returnAllNodes() {
			const result = await this.httpHelper("RUNTIME", "GET", "nodes/returnAllNodeDefinitions", {});
			if (!result?.error) {
				const internalOnly = ["aiIntegrationSandbox", "aiWorkflowTemplate"];
				const filtered = (result.response || []).filter(item => !internalOnly.includes(item.nodesObjectPropertyName));
				this.availableIntegrations = Object.freeze(filtered);
			}
		},
		async selectIntegration(nodeName) {
			const internalOnly = ["aiIntegrationSandbox", "aiWorkflowTemplate"];
			if (internalOnly.includes(nodeName)) {
				this.selectedIntegration = null;
				this.loadingDocs = false;
				return;
			}
			this.selectedIntegration = nodeName;
			this.loadingDocs = true;
			this.documentationContent = "";
			
			if (this.isMobileView) {
				this.showSidebar = false;
			}

			// Update URL without reloading
			if (nodeName) {
				if (this.$route.query.integration !== nodeName) {
					this.$router.replace({ query: { ...this.$route.query, integration: nodeName } }).catch(() => { });
				}
			} else {
				if (this.$route.query.integration) {
					const newQuery = { ...this.$route.query };
					delete newQuery.integration;
					this.$router.replace({ query: newQuery }).catch(() => { });
				}
			}

			// Scroll active item into view without shifting the page
			this.$nextTick(() => {
				const listContainer = this.$el.querySelector('.integration-list');
				const activeEl = this.$el.querySelector('.integration-item.active');
				if (listContainer && activeEl) {
					const scrollPos = activeEl.offsetTop - listContainer.offsetTop - (listContainer.offsetHeight / 2) + (activeEl.offsetHeight / 2);
					listContainer.scrollTo({
						top: scrollPos,
						behavior: 'smooth'
					});
				}
			});

			if (!nodeName) {
				this.loadingDocs = false;
				return;
			}

			const result = await this.httpHelper("RUNTIME", "GET", `nodes/documentation/${nodeName}`, {});
			this.loadingDocs = false;
			if (!result?.error && result.response?.documentation) {
				this.documentationContent = result.response.documentation;
			} else {
				this.documentationContent = "Documentation not available yet for this integration.";
			}
		},
		async copyDocumentation() {
			try {
				await navigator.clipboard.writeText(this.documentationContent);
				this.$message({
					message: 'Documentation copied to clipboard',
					type: 'success'
				});
			} catch (err) {
				this.$message.error('Failed to copy documentation');
			}
		},
	},
};
</script>

<style scoped>
.mobile-menu-btn {
	display: none;
	font-size: 24px;
	cursor: pointer;
	color: #1f2d3d;
	margin-right: 15px;
}
.sidebar-overlay {
	display: none;
}
@media (max-width: 768px) {
	.mobile-menu-btn {
		display: block;
	}
	.hide-on-mobile {
		display: none;
	}
	.docs-sidebar {
		position: absolute;
		left: 0;
		top: 0;
		height: 100% !important;
		transform: translateX(-100%);
		transition: transform 0.3s ease;
		z-index: 1000;
		box-shadow: 2px 0 8px rgba(0,0,0,0.1);
	}
	.docs-sidebar.sidebar-open {
		transform: translateX(0);
	}
	.sidebar-overlay {
		display: block;
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0,0,0,0.5);
		z-index: 999;
	}
}

.compressedDivider {
	margin-top: 10px;
	margin-bottom: 0px;
}

.integration-list {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

/* Custom scrollbar for sidebar */
.integration-list::-webkit-scrollbar {
	width: 6px;
}

.integration-list::-webkit-scrollbar-thumb {
	background-color: #dcdfe6;
	border-radius: 3px;
}

.copy-btn {
	background-color: #303133;
	border-color: #303133;
	color: #fff;
}

.copy-btn:hover,
.copy-btn:focus {
	background-color: #1a1a1a;
	border-color: #1a1a1a;
	color: #fff;
}

.integration-item {
	display: flex;
	align-items: center;
	padding: 10px 20px;
	border-radius: 6px;
	cursor: pointer;
	transition: background-color 0.2s, color 0.2s;
	font-size: 14px;
	color: #606266;
}

.integration-item:hover {
	background-color: #f2f6fc;
}

.integration-item.active {
	background-color: transparent;
	color: #1f2d3d;
	font-weight: 700;
}

.integration-item.active .integration-icon {
	opacity: 0.8;
}

.docs-search>>>.el-input__inner {
	border-radius: 20px;
}

.integration-icon {
	width: 24px;
	height: 24px;
	margin-right: 12px;
	object-fit: contain;
}

.empty-state,
.loading-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100%;
	color: #909399;
	font-size: 16px;
}

.empty-state p,
.loading-state p {
	margin-top: 16px;
}

/* Markdown Styles */
.markdown-content {
	line-height: 1.6;
	color: #303133;
	font-size: 15px;
	padding-bottom: 40px;
}

.markdown-content>>>h1 {
	font-size: 28px;
	border-bottom: 1px solid #ebeef5;
	padding-bottom: 12px;
	margin-top: 0;
	margin-bottom: 24px;
	font-weight: 600;
}

.markdown-content>>>h2 {
	font-size: 20px;
	margin-top: 32px;
	margin-bottom: 16px;
	font-weight: 600;
}

.markdown-content>>>h3 {
	font-size: 16px;
	margin-top: 24px;
	margin-bottom: 12px;
	font-weight: 600;
}

.markdown-content>>>p {
	margin-top: 0;
	margin-bottom: 16px;
}

.markdown-content>>>ul {
	padding-left: 24px;
	margin-bottom: 16px;
}

.markdown-content>>>li {
	margin-bottom: 8px;
}

.markdown-content>>>strong {
	font-weight: 600;
	color: #1f2d3d;
}

.markdown-content>>>a {
	color: #409eff;
	text-decoration: none;
}

.markdown-content>>>a:hover {
	text-decoration: underline;
}

.markdown-content>>>code {
	padding: 3px 6px;
	font-size: 13px;
	color: #e01e5a;
	background-color: #f9f2f4;
	border-radius: 4px;
	font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
}

.documentation-page-root,
.documentation-page-root p,
.documentation-page-root h1,
.documentation-page-root h2,
.documentation-page-root h3,
.documentation-page-root span,
.documentation-page-root div,
.documentation-page-root input {
	font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif !important;
}
</style>
